import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Box, Typography, Divider } from '@mui/material';

// api
import MessagesApi from '../api/messages.api';
import { useAppSelector } from '../hooks/hooks';

// comps
import HomeCarousel from '../components/layout/homeCarousel';

// icons / img
import { ReactComponent as MapIcon } from '../assets/icons/map-icon.svg';
import { ReactComponent as CommentIcon } from '../assets/icons/comment-icon.svg';

// constants
import { colors } from '../theme';
import { reportListItems, manageListItems, roomListItems, mainListItems, eventListItems, hasRouteAccess } from '../constants/routes';
import { MOMENT_DATE_FORMAT } from '../constants/date_format';
import { FACILITIES_VIEW_TYPE } from '../constants';
import { accountTypes } from '../constants';

interface ItemProps {
    isSpecialMsg: boolean;
}

interface BottomProps {
    isSingleRow?: boolean;
}

const Home = (props: any) => {
    let navigate = useNavigate();
    const _messagesApi = new MessagesApi();
    const user = useAppSelector((state) => state.user);
    const appState = useAppSelector((state) => state.appState);
    const today = moment().format(MOMENT_DATE_FORMAT);

    const year = new Date().getFullYear();
    const isInstructor = user?.accountType === accountTypes.instructor;

    const facilitiesCards = [
        { item: roomListItems[1], title: `Room ${roomListItems[1].title}`, description: 'Manage room availability and conflicts' },
        { item: roomListItems[0], title: `Room ${roomListItems[0].title}`, description: 'Manage all rooms' },
        { item: reportListItems[0], title: reportListItems[0].title, description: 'Generate a grid for daily schedule' },
        appState.campus?.id === 2
            ? { item: manageListItems[0], title: manageListItems[0].title, description: 'Manage long-term course listing' }
            : { item: mainListItems[1], title: mainListItems[1].title, description: 'Generate timetables for a set or an instructor' },
    ];

    const defaultCards = [
        { item: mainListItems[3], title: `${mainListItems[3].title} & Appointments`, description: 'Manage office hours and student appointments' },
        isInstructor
            ? { item: mainListItems[4], title: 'Personal Information', description: 'Edit your personal information. Customize the public note displayed on your timetable' }
            : { item: manageListItems[2], title: 'Instructor Form', description: 'Manage and add new instructors' },
        { item: mainListItems[1], title: mainListItems[1].title, description: 'Generate timetables for a set or an instructor' },
        { item: roomListItems[1], title: `Room ${roomListItems[1].title}`, description: 'Manage room availability and conflicts' },
        { item: eventListItems[0], title: eventListItems[0].title, description: 'Search and manage Banner events' },
        { item: reportListItems[0], title: reportListItems[0].title, description: 'Generate a grid for daily schedule' },
    ];

    const navCards = (appState.viewType === FACILITIES_VIEW_TYPE ? facilitiesCards : defaultCards)
        .filter((card) => hasRouteAccess(card.item, appState.viewType, user?.accountType))
        .slice(0, 4);

    const renderNavCards = (cards: Array<any>) =>
        cards.map((card, i) => (
            <NavCard key={card.item.route} onClick={() => navigate(card.item.route)} style={i === 0 ? { marginRight: '1em' } : null}>
                <Typography variant="h4">{card.title}</Typography>
                <Typography variant="body1">{card.description}</Typography>
            </NavCard>
        ));

    const [state, setState] = useState({
        specialMessage: null,
        dataSourceStatus: {
            isAllCurrent: false,
            dataDate: null,
        },
    });

    useEffect(() => {
        const loadData = async (campusId: number) => {
            const specialMessage = await _messagesApi.GetMessageByDate(moment().toDate());

            setState({
                ...state,
                specialMessage,
            });
        };

        if (appState.campus?.id) {
            loadData(appState.campus.id).catch((error) => toast.error('Error loading term data'));
        }
    }, [appState.campus]);

    return (
        <Wrapper>
            <SectionWrapper>
                <Container>
                    {appState.viewType === FACILITIES_VIEW_TYPE ? (
                        <Top>
                            <LeftItem sx={{ flex: 1, mr: '1.5em' }} isSpecialMsg={false}>
                                <MapIcon />
                                <Typography sx={{ marginTop: '5px' }}>
                                    <span>{appState.campus?.name}</span>
                                </Typography>
                            </LeftItem>

                            <LeftItem sx={{ flex: 3 }} isSpecialMsg={true}>
                                <FlexBox>
                                    <CommentIcon />
                                    {state.specialMessage ? (
                                        <Typography>
                                            <span>{state.specialMessage.message}</span>
                                        </Typography>
                                    ) : (
                                        <Typography variant="h5" sx={{ fontSize: '14px', margin: '0px 10px' }}>
                                            No Special Message to display
                                        </Typography>
                                    )}
                                </FlexBox>
                                <Typography variant="h5" sx={{ whiteSpace: 'noWrap' }}>
                                    {today}
                                </Typography>
                            </LeftItem>
                        </Top>
                    ) : (
                        <HomeCarousel />
                    )}
                    <Bottom isSingleRow={navCards.length <= 2}>
                        <Typography variant="h2"> Manage </Typography>
                        <div>{renderNavCards(navCards.slice(0, 2))}</div>
                        {navCards.length > 2 && <div>{renderNavCards(navCards.slice(2))}</div>}
                    </Bottom>
                    <Divider sx={{ padding: '1.5em 0px' }} />
                    <FlexBox
                        sx={{
                            justifyContent: 'space-between',
                            'p, a': {
                                color: colors.grey.dark,
                                marginTop: '1em',
                            },
                        }}
                    >
                        <Typography>© {year} BCIT. All rights reserved</Typography>
                        <Typography>
                            Contact <a href="mailto:ttportal@bcit.ca">ttportal@bcit.ca</a> for technical support.
                        </Typography>
                    </FlexBox>
                </Container>
            </SectionWrapper>
        </Wrapper>
    );
};

export default Home;

const Wrapper = styled.div`
    max-width: 100%;
    height: calc(100vh - 75px);
    display: flex;
    justify-content: center;
    align-items: center;
`;

const SectionWrapper = styled.div`
    width: 100%;
    height: 100%;
    max-height: 610px;
    max-width: 975px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px 15px;

    h2 {
        color: ${colors.navy.main};
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: space-between;
    flex: 2.5;
`;

const Top = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const LeftItem = styled(Box)<ItemProps>`
    background-color: ${colors.white};
    border-radius: 0px 10px 10px 0px;
    border-left: 5px solid ${colors.navy.main};
    box-shadow: ${colors.boxShadow};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    padding: 15px;

    svg {
        min-width: 32px;
        height: auto;
        margin-bottom: 5px;

        path {
            fill: ${colors.navy.main};
        }
    }

    p {
        span {
            font-weight: 500;
        }
        color: ${colors.grey.dark};
    }

    h5 {
        font-weight: 400;
        color: ${colors.grey.medium};
        /* margin-top: 5px; */
    }

    ${(props) =>
        props.isSpecialMsg && {
            justifyContent: 'space-between',
            borderLeft: `5px solid ${colors.green.main}`,
            flexDirection: 'row',
            svg: {
                marginBottom: 0,
                path: {
                    fill: colors.green.main,
                },
            },
            p: {
                color: colors.green.main,
                backgroundColor: colors.green.light,
                margin: '0px 10px',
                padding: ' 5px 10px',
                span: {
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                },
            },
        }}
`;

const Bottom = styled.div<BottomProps>`
    height: ${(props) => !props.isSingleRow && '55%;'};
    margin-top: 1em;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    & > div:first-of-type,
    & > div:nth-of-type(2) {
        justify-content: space-between;
        display: flex;
        margin-top: 1em;
    }
`;

const NavCard = styled.div`
    width: 100%;
    min-height: 7vh;
    height: 100%;
    padding: 1.5em;
    background: white;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    h4 {
        letter-spacing: -0.01em;
        color: ${colors.navy.main};
        margin-bottom: 10px;
    }

    p {
        word-wrap: break-word;
        text-align: center;
    }

    &:hover {
        cursor: pointer;
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);

        & > * {
            color: ${colors.blue.main};
        }
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
`;
